import {
	Box,
	Flex,
	HStack,
	VStack,
	Text,
	Badge,
	Icon,
	Skeleton,
	Button,
	FormControl,
	FormLabel,
	FormErrorMessage,
	FormHelperText,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
	Textarea,
	Stat,
	StatLabel,
	StatNumber,
	StatHelpText,
	Tooltip,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	TableContainer,
	Kbd,
} from '@chakra-ui/react';
import { useRef, useState, useEffect } from 'react';
import {
	FiInbox,
	FiAlertTriangle,
	FiHelpCircle,
	FiInfo,
	FiCheckCircle,
	FiAlertCircle,
	FiSearch,
} from 'react-icons/fi';
// App-wide shared pagination — finance-core reuses it via the Pagination adapter below.
import TopPagination from 'components/pagination/TopPagination';
import { useFinanceUI, statusLabel } from './ui';
import SearchOmnibox, { useSearchOmnibox } from './SearchOmnibox';

/* ───────────────────────────── Page shell ───────────────────────────── */
/**
 * Every finance-core screen is wrapped in this, which is why the global search
 * omnibox (PR-44) is mounted here: ⌘K reaches a document from ANY of the forty
 * modules without each one having to wire it up, and without the shortcut
 * leaking into the rest of the app.
 */
/**
 * @param {boolean} [search]  mount the finance omnibox. Defaults to true, which
 *   is what every finance module wants. ★ SCREENS OUTSIDE FINANCE MUST PASS
 *   FALSE: the header comment above promises the shortcut does not leak into
 *   the rest of the app, and that only holds while this shell is only used by
 *   finance. The WhatsApp campaign screens reuse it for layout, and were
 *   offering "Search anything in finance" — and swallowing ⌘K — on a WhatsApp
 *   page.
 */
export function FinancePage({ title, subtitle, icon, actions, children, search = true }) {
	const ui = useFinanceUI();
	const omnibox = useSearchOmnibox({ enabled: search });
	return (
		<Box p={{ base: 4, md: 6 }} bg={ui.pageBg} minH='calc(100vh - 90px)'>
			{search && <SearchOmnibox isOpen={omnibox.isOpen} onClose={omnibox.onClose} />}
			<Flex justify='space-between' align='flex-start' mb={5} gap={4} wrap='wrap'>
				<HStack align='center' spacing={3}>
					{icon && (
						<Flex
							align='center'
							justify='center'
							boxSize='42px'
							borderRadius='lg'
							bg={ui.accentSubtle}
							flexShrink={0}
						>
							<Icon as={icon} boxSize={5} color={ui.accent} />
						</Flex>
					)}
					<Box>
						<Text fontSize='2xl' fontWeight='bold' color={ui.textPrimary} lineHeight='1.2'>
							{title}
						</Text>
						{subtitle && (
							<Text fontSize='sm' color={ui.textSecondary} mt={0.5}>
								{subtitle}
							</Text>
						)}
					</Box>
				</HStack>
				<HStack spacing={2}>
					{search && (
					<Tooltip label='Search anything in finance' hasArrow>
						<Button
							size='sm'
							variant='outline'
							leftIcon={<Icon as={FiSearch} />}
							color={ui.textSecondary}
							borderColor={ui.border}
							_hover={{ bg: ui.surfaceSubtle }}
							onClick={omnibox.onOpen}
						>
							Search
							<Kbd ml={2} fontSize='0.65em'>⌘K</Kbd>
						</Button>
					</Tooltip>
					)}
					{actions}
				</HStack>
			</Flex>
			{children}
		</Box>
	);
}

/* ───────────────────────────── Section card ─────────────────────────── */
export function SectionCard({ title, subtitle, actions, children, p = 5, ...rest }) {
	const ui = useFinanceUI();
	return (
		<Box
			bg={ui.surface}
			borderWidth='1px'
			borderColor={ui.border}
			borderRadius='xl'
			boxShadow={ui.shadowCard}
			overflow='hidden'
			{...rest}
		>
			{(title || actions) && (
				<Flex
					justify='space-between'
					align='center'
					px={5}
					py={4}
					borderBottomWidth='1px'
					borderColor={ui.borderSubtle}
					gap={3}
					wrap='wrap'
				>
					<Box>
						{title && (
							<Text fontWeight='semibold' color={ui.textPrimary}>
								{title}
							</Text>
						)}
						{subtitle && (
							<Text fontSize='xs' color={ui.textMuted}>
								{subtitle}
							</Text>
						)}
					</Box>
					{actions && <HStack spacing={2}>{actions}</HStack>}
				</Flex>
			)}
			<Box p={p}>{children}</Box>
		</Box>
	);
}

/* ───────────────────────────── Stat card ────────────────────────────── */
export function StatCard({ label, value, help, icon, accent }) {
	const ui = useFinanceUI();
	return (
		<Box
			bg={ui.surface}
			borderWidth='1px'
			borderColor={ui.border}
			borderRadius='xl'
			boxShadow={ui.shadowCard}
			p={5}
		>
			<Flex justify='space-between' align='flex-start'>
				<Stat>
					<StatLabel color={ui.textSecondary} fontSize='sm'>
						{label}
					</StatLabel>
					<StatNumber color={ui.textPrimary}>{value}</StatNumber>
					{help && <StatHelpText color={ui.textMuted} mb={0}>{help}</StatHelpText>}
				</Stat>
				{icon && <Icon as={icon} boxSize={6} color={accent || ui.accent} />}
			</Flex>
		</Box>
	);
}

/* ───────────────────────────── Status badge ─────────────────────────── */
// Maps each status to one of our theme-safe semantic colors (keys on useFinanceUI).
const STATUS_TONE = {
	// positive / final-good
	POSTED: 'success', PAID: 'success', CLEARED: 'success', RECONCILED: 'success',
	ALLOCATED: 'success', ACTIVE: 'success', OPEN: 'success', REVENUE: 'success',
	APPROVED: 'success', DONE: 'success', SETTLED: 'success', COMPLETED: 'success',
	CONVERTED: 'success', AWARDED: 'success', RECEIVED_FULL: 'success',
	// neutral
	DRAFT: 'muted', CANCELLED: 'muted', INACTIVE: 'muted', CLOSED: 'muted',
	SUPERSEDED: 'muted', DISMISSED: 'muted', ARCHIVED: 'muted', WRITTEN_OFF: 'muted',
	// info (in-progress)
	SENT: 'info', RECEIVED: 'info', DEPOSITED: 'info', ASSET: 'info',
	PENDING: 'info', ELIGIBLE: 'info', SUBMITTED: 'info', UPCOMING: 'info',
	PENDING_APPROVAL: 'info', QUOTED: 'info', IN_TRANSIT: 'info', REVIEWED: 'info',
	// warning
	PARTIALLY_PAID: 'warning', UNDER_CLEARING: 'warning', REVERSED: 'warning', LIABILITY: 'warning',
	AWAITING_COLLECTION: 'warning', UNDER_REVIEW: 'warning', DUE: 'warning',
	PARTIALLY_RECEIVED: 'warning', PARTIALLY_SETTLED: 'warning', ON_HOLD: 'warning',
	SUSPENDED: 'warning', DISPUTED: 'warning', EXPIRED: 'warning',
	// danger
	OVERDUE: 'danger', RETURNED: 'danger', LOCKED: 'danger', EXPENSE: 'danger', REJECTED: 'danger',
	BLOCKED: 'danger', DISPOSED: 'danger',
	// accent
	CREDITED: 'accent', REPLACED: 'accent', EQUITY: 'accent', IN_PAYMENT_BATCH: 'accent',

	// ── WhatsApp campaign engine ──────────────────────────────────────────
	// Campaign lifecycle. RUNNING is `accent` rather than `success`: it is
	// happening, not finished, and a green badge on a campaign that is still
	// spending money reads as "all done".
	RUNNING: 'accent', PAUSED: 'warning', FAILED_TO_PLAN: 'danger',
	// Recipient ladder. Everything up to DELIVERED is in flight; REPLIED is the
	// outcome a campaign actually wants, so it is the only `success` here.
	QUEUED: 'muted', SENDING: 'info', DELIVERED: 'info', READ: 'info',
	REPLIED: 'success',
	// Not failures, and deliberately not red: a skipped contact was protected by
	// a safety rule, and an opt-out is the system working correctly.
	SKIPPED: 'muted', OPTED_OUT: 'warning',
	// Sent, charged for, and never confirmed delivered — offline or blocked.
	// Meta never says which, so this must not read as an error.
	UNDELIVERED: 'warning',
};
const TONE_TO_UI = { success: 'success', danger: 'danger', warning: 'warning', info: 'info', accent: 'accent', muted: 'textMuted' };

export function StatusBadge({ status, ...rest }) {
	const ui = useFinanceUI();
	const tone = STATUS_TONE[status] || 'muted';
	const color = ui[TONE_TO_UI[tone]];
	return (
		<HStack
			as='span'
			display='inline-flex'
			spacing={1.5}
			bg={ui.surfaceElevated}
			borderWidth='1px'
			borderColor={ui.border}
			borderRadius='full'
			px={2.5}
			py={0.5}
			{...rest}
		>
			<Box as='span' boxSize='6px' borderRadius='full' bg={color} flexShrink={0} />
			<Text as='span' fontSize='0.72rem' fontWeight='semibold' color={color} lineHeight='1.2' whiteSpace='nowrap'>
				{statusLabel(status)}
			</Text>
		</HStack>
	);
}

/* ───────────────────────────── Empty state ──────────────────────────── */
export function EmptyState({ icon = FiInbox, title = 'Nothing here yet', description, action }) {
	const ui = useFinanceUI();
	return (
		<VStack py={14} spacing={3}>
			<Flex align='center' justify='center' boxSize='56px' borderRadius='full' bg={ui.surfaceSubtle}>
				<Icon as={icon} boxSize={7} color={ui.iconMuted} />
			</Flex>
			<Text fontWeight='semibold' color={ui.textPrimary}>
				{title}
			</Text>
			{description && (
				<Text fontSize='sm' color={ui.textSecondary} maxW='420px' textAlign='center'>
					{description}
				</Text>
			)}
			{/* `action` renders exactly as the caller passed it — same canonical
			    action button as a page/FilterBar CTA (typically colorScheme='brand').
			    This wrapper is layout-only and must not impose color/variant. */}
			{action && <Box pt={1}>{action}</Box>}
		</VStack>
	);
}

/* ───────────────────────────── Error state ──────────────────────────── */
export function ErrorState({ title = 'Something went wrong', description, onRetry }) {
	const ui = useFinanceUI();
	return (
		<VStack py={14} spacing={3}>
			<Icon as={FiAlertTriangle} boxSize={8} color={ui.danger} />
			<Text fontWeight='semibold' color={ui.textPrimary}>
				{title}
			</Text>
			{description && (
				<Text fontSize='sm' color={ui.textSecondary} maxW='420px' textAlign='center'>
					{description}
				</Text>
			)}
			{onRetry && (
				<Button size='sm' onClick={onRetry} variant='outline'>
					Try again
				</Button>
			)}
		</VStack>
	);
}

/* ───────────────────────────── Table skeleton ───────────────────────── */
export function TableSkeleton({ rows = 6, cols = 5 }) {
	const ui = useFinanceUI();
	return (
		<TableContainer>
			<Table size='sm'>
				<Thead bg='erp.table.header'>
					<Tr>
						{Array.from({ length: cols }).map((_, i) => (
							<Th key={i} borderColor={ui.borderSubtle}>
								<Skeleton height='10px' width='60%' />
							</Th>
						))}
					</Tr>
				</Thead>
				<Tbody>
					{Array.from({ length: rows }).map((_, r) => (
						<Tr key={r}>
							{Array.from({ length: cols }).map((_, c) => (
								<Td key={c} borderColor={ui.borderSubtle}>
									<Skeleton height='12px' />
								</Td>
							))}
						</Tr>
					))}
				</Tbody>
			</Table>
		</TableContainer>
	);
}

/* ───────────────────────────── Pagination ───────────────────────────── */
/**
 * Reusable top pagination bar for finance-core lists. Shows the visible range,
 * an optional page-size selector, and First / Prev / Next / Last controls.
 * Renders nothing when there is nothing to page. Theme-native (dark navy kit).
 *
 * Props: { page, totalPages, total, limit, onPageChange, onLimitChange?,
 *          loading?, pageSizes?, compact? }
 */
export function Pagination({
	page = 1,
	totalPages = 1,
	total = 0,
	limit = 20,
	onPageChange,
	onLimitChange,
	loading = false,
	compact = false,
	...rest
}) {
	if (!total) return null;

	// Thin adapter over the app-wide shared TopPagination component
	// (components/pagination/TopPagination) so finance-core paginates exactly
	// like every other module. This kit's prop names are preserved, so all
	// finance screens keep working unchanged.
	return (
		<Flex px={compact ? 0 : 4} py={compact ? 0 : 2} {...rest}>
			<TopPagination
				currentPage={page}
				totalPages={totalPages || 1}
				totalItems={total}
				itemsPerPage={limit}
				onPageChange={(p) => onPageChange && onPageChange(Math.max(1, Math.min(p, totalPages || 1)))}
				handlePageSize={onLimitChange ? (size) => onLimitChange(Number(size)) : undefined}
				pageLimit={!!onLimitChange}
				loading={loading}
				refetching={loading}
				sizeMedium
			/>
		</Flex>
	);
}

/* ───────────────────────────── Inline banner ────────────────────────── */
const BANNER_META = {
	info: { icon: FiInfo, color: 'info' },
	success: { icon: FiCheckCircle, color: 'success' },
	warning: { icon: FiAlertTriangle, color: 'warning' },
	error: { icon: FiAlertCircle, color: 'danger' },
};
export function Banner({ status = 'info', title, description, children, ...rest }) {
	const ui = useFinanceUI();
	const meta = BANNER_META[status] || BANNER_META.info;
	const accent = ui[meta.color];
	return (
		<Box
			bg={ui.surfaceElevated}
			borderWidth='1px'
			borderColor={ui.border}
			borderLeftWidth='3px'
			borderLeftColor={accent}
			borderRadius='md'
			px={4}
			py={3}
			{...rest}
		>
			<HStack align='flex-start' spacing={3}>
				<Icon as={meta.icon} color={accent} boxSize={4} mt={0.5} flexShrink={0} />
				<Box>
					{title && (
						<Text fontSize='sm' fontWeight='semibold' color={ui.textPrimary}>
							{title}
						</Text>
					)}
					{description && (
						<Text fontSize='sm' color={ui.textSecondary}>
							{description}
						</Text>
					)}
					{children}
				</Box>
			</HStack>
		</Box>
	);
}

/* ───────────────────────────── Form field ───────────────────────────── */
export function Field({ label, isRequired, error, help, children, tooltip, id }) {
	const ui = useFinanceUI();
	return (
		// `id` is optional. It exists so a control that renders its OWN FormControl
		// (DateField -> CustomDatePicker) can share this one's id, which is what
		// wires the FormLabel's htmlFor to the real input across the nesting.
		<FormControl isRequired={isRequired} isInvalid={!!error} id={id}>
			{label && (
				<FormLabel color={ui.textSecondary} fontSize='sm' fontWeight='medium' mb={1.5} display='flex' alignItems='center'>
					{label}
					{tooltip && (
						<Tooltip label={tooltip} hasArrow placement='top'>
							<Box as='span' ml={1.5} display='inline-flex'>
								<Icon as={FiHelpCircle} boxSize={3.5} color={ui.iconMuted} />
							</Box>
						</Tooltip>
					)}
				</FormLabel>
			)}
			{children}
			{error ? (
				<FormErrorMessage fontSize='xs'>{error}</FormErrorMessage>
			) : (
				help && (
					<FormHelperText color={ui.textMuted} fontSize='xs'>
						{help}
					</FormHelperText>
				)
			)}
		</FormControl>
	);
}

/* ───────────────────────────── Confirm dialog ───────────────────────── */
/**
 * Accessible confirmation dialog. Replaces window.confirm / window.prompt.
 * When requireReason is set, shows a mandatory reason box and passes the reason
 * string to onConfirm(reason).
 */
export function ConfirmDialog({
	isOpen,
	onClose,
	onConfirm,
	title = 'Are you sure?',
	description,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	confirmScheme = 'red',
	isLoading = false,
	requireReason = false,
	reasonLabel = 'Reason',
	reasonPlaceholder = 'Add a short reason (kept in the audit log)…',
}) {
	const ui = useFinanceUI();
	const cancelRef = useRef();
	const [reason, setReason] = useState('');

	useEffect(() => {
		if (isOpen) setReason('');
	}, [isOpen]);

	const disabled = requireReason && !reason.trim();

	return (
		<AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
			<AlertDialogOverlay backdropFilter='blur(3px)'>
				<AlertDialogContent bg={ui.surface} borderColor={ui.border} borderWidth='1px'>
					<AlertDialogHeader color={ui.textPrimary} fontSize='lg' fontWeight='bold'>
						{title}
					</AlertDialogHeader>
					<AlertDialogBody>
						{description && (
							<Text color={ui.textSecondary} fontSize='sm' mb={requireReason ? 3 : 0}>
								{description}
							</Text>
						)}
						{requireReason && (
							<FormControl isRequired>
								<FormLabel color={ui.textSecondary} fontSize='sm'>
									{reasonLabel}
								</FormLabel>
								<Textarea
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									placeholder={reasonPlaceholder}
									bg={ui.inputBg}
									color={ui.textPrimary}
									borderColor={ui.border}
									rows={3}
									autoFocus
								/>
							</FormControl>
						)}
					</AlertDialogBody>
					<AlertDialogFooter>
						<Button ref={cancelRef} onClick={onClose} variant='ghost' color={ui.textSecondary}>
							{cancelLabel}
						</Button>
						<Button
							colorScheme={confirmScheme}
							ml={3}
							isLoading={isLoading}
							isDisabled={disabled}
							onClick={() => onConfirm(reason)}
						>
							{confirmLabel}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialogOverlay>
		</AlertDialog>
	);
}
